/**
 * CopilotPage.jsx
 * ===============
 * Sprint-1 Copilot UI. Renders when URL has ?copilot=1.
 *
 * Fetches GET /api/copilot/queue?size=8 and shows a compact row-based view
 * of the prioritized daily action queue.
 *
 * Design mirrors the outreach tracker Alon uses today: dense rows, not cards.
 * One row per prospect. Click ▼ to expand for score breakdown + context.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DealDrawer from './DealDrawer';
import { useAuth } from '../contexts/AuthContext';

/** Map allowed dashboard emails → Copilot viewer id. Everyone else 403s. */
const EMAIL_TO_VIEWER = Object.freeze({
  'alon@octup.com': 'alon',
  'jay@octup.com':  'jay',
  // Include admin for local testing convenience; remove for production hardening.
  'admin@example.com': 'alon',
});

// Local dev: backend runs on :8080. In production it's same-origin so leave empty.
// Override with REACT_APP_COPILOT_API if the backend lives elsewhere.
const API_BASE = process.env.REACT_APP_COPILOT_API
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? 'http://localhost:8080'
        : '');

// Design tokens (match Octup brand palette from CEODashboardMockup).
const COLORS = {
  navy:   '#0B1B3A',
  teal:   '#00CBC0',
  pink:   '#FF3489',
  purple: '#743CF7',
  amber:  '#FBBF24',
  red:    '#EF4444',
  gray50: '#F9FAFB',
  gray100:'#F3F4F6',
  gray400:'#9CA3AF',
  gray500:'#6B7280',
  gray700:'#374151',
};

function classNames(...cls) { return cls.filter(Boolean).join(' '); }
function usd(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${Math.round(v / 1_000)}K`;
  return `$${v.toFixed(0)}`;
}
function metroLabel(m) {
  if (!m) return 'other';
  return m.replace(/^metro_/, '').replace(/^state_/, '').replace(/_/g, ' ');
}
function metroBadgeColor(m) {
  if (m === 'metro_la' || m === 'metro_orange_county') return { bg: 'rgba(0,203,192,0.12)',  color: COLORS.teal };
  if (m === 'metro_austin')                            return { bg: 'rgba(255,52,137,0.12)', color: COLORS.pink };
  if (m && m.startsWith('state_'))                     return { bg: 'rgba(116,60,247,0.10)', color: COLORS.purple };
  return { bg: COLORS.gray100, color: COLORS.gray500 };
}
function actionBadge(action) {
  switch (String(action || '').toLowerCase()) {
    case 'call':     return { icon: '☎', bg: 'rgba(255,52,137,0.12)',  color: COLORS.pink };
    case 'email':    return { icon: '✉', bg: 'rgba(0,203,192,0.12)',   color: COLORS.teal };
    case 'linkedin': return { icon: 'in',bg: 'rgba(116,60,247,0.12)',  color: COLORS.purple };
    case 'sms':      return { icon: '💬',bg: 'rgba(251,191,36,0.14)',  color: '#B45309' };
    default:         return { icon: '•', bg: COLORS.gray100,           color: COLORS.gray500 };
  }
}

export default function CopilotPage() {
  const auth = useAuth();
  const loggedInEmail = String(auth?.user?.email || '').toLowerCase();
  const allowedViewer = EMAIL_TO_VIEWER[loggedInEmail] || null;

  const [size, setSize] = useState(8);
  // Default viewer = the mapping for the logged-in user. Fall back to 'alon'
  // if not mapped (the not-mapped case is caught by the gate below).
  const [viewer, setViewer] = useState(allowedViewer || 'alon');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [pending, setPending] = useState(null);
  const [lastTouchForUndo, setLastTouchForUndo] = useState(null); // { touch_id, deal_id, ttl }
  const [drawerItem, setDrawerItem] = useState(null); // hoisted so other callbacks can setDrawerItem
  const [draftOpen, setDraftOpen] = useState(null);
  const [toast, setToast] = useState(null);

  // Hoisted above every callback that uses it. Referring to `showToast` from
  // within a useCallback body relies on lexical scope — but production bundlers
  // (Terser) minify identifiers to single letters and the TDZ rule then
  // triggers if the const is declared AFTER the callback in source order.
  const showToast = useCallback((message, kind = 'ok') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bust = Date.now();
      const resp = await fetch(`${API_BASE}/api/copilot/queue?size=${size}&_t=${bust}`, {
        cache: 'no-store',
        headers: {
          'X-Copilot-User': viewer,
          'X-Copilot-Email': loggedInEmail || '',
          'X-Copilot-Trace-Id': `ui_${bust.toString(36)}`,
        },
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`${resp.status}: ${body.slice(0, 200)}`);
      }
      const data = await resp.json();
      setPayload(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [size, viewer]);

  // Load pending next-actions alongside the queue.
  const loadPending = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/copilot/pending-actions?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'X-Copilot-User': viewer, 'X-Copilot-Email': loggedInEmail || '' },
      });
      if (!resp.ok) return;
      setPending(await resp.json());
    } catch (_e) { /* silent */ }
  }, [viewer, loggedInEmail]);

  const resolvePending = useCallback(async (touchId) => {
    try {
      const resp = await fetch(`${API_BASE}/api/copilot/pending-actions/${touchId}/resolve`, {
        method: 'POST',
        headers: { 'X-Copilot-User': viewer, 'X-Copilot-Email': loggedInEmail || '' },
      });
      if (resp.ok) loadPending();
    } catch (_e) { /* silent */ }
  }, [viewer, loggedInEmail, loadPending]);

  const undoTouch = useCallback(async (touchId) => {
    try {
      const resp = await fetch(`${API_BASE}/api/copilot/touch/${touchId}`, {
        method: 'DELETE',
        headers: { 'X-Copilot-User': viewer, 'X-Copilot-Email': loggedInEmail || '' },
      });
      if (resp.ok) {
        setLastTouchForUndo(null);
        loadPending();
      }
    } catch (_e) { /* silent */ }
  }, [viewer, loggedInEmail, loadPending]);

  const logTouch = useCallback(async ({ dealId, touchType, notes }) => {
    try {
      const resp = await fetch(`${API_BASE}/api/copilot/touch`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'X-Copilot-User': viewer,
          'X-Copilot-Trace-Id': `ui_touch_${Date.now().toString(36)}`,
        },
        body: JSON.stringify({ deal_id: dealId, touch_type: touchType, notes: notes || '' }),
      });
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`${resp.status}: ${body.slice(0, 200)}`);
      }
      const data = await resp.json();
      showToast(`✓ Logged ${touchType.replace('_', ' ')}`);
      setLastTouchForUndo({ touch_id: data.touch_id, deal_id: dealId, at: Date.now() });
      loadPending();
    } catch (e) {
      showToast(`Failed: ${e.message}`, 'err');
    }
  }, [viewer, loadPending]);

  const openDraft = useCallback(async ({ dealId, channel = 'email', intent = 'initial_reengage' }) => {
    setDraftOpen({ deal_id: dealId, loading: true, data: null, error: null });
    try {
      const resp = await fetch(`${API_BASE}/api/copilot/draft`, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'X-Copilot-User': viewer,
          'X-Copilot-Trace-Id': `ui_draft_${Date.now().toString(36)}`,
        },
        body: JSON.stringify({
          deal_id: dealId,
          channel,
          intent,
          voice_of: viewer,
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setDraftOpen({ deal_id: dealId, loading: false, data: null, error: body.message || `HTTP ${resp.status}` });
        return;
      }
      setDraftOpen({ deal_id: dealId, loading: false, data: body, error: null });
    } catch (e) {
      setDraftOpen({ deal_id: dealId, loading: false, data: null, error: e.message });
    }
  }, [viewer]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const stats = useMemo(() => {
    if (!payload?.items) return null;
    const items = payload.items;
    return {
      total: items.length,
      totalArr: items.reduce((s, i) => s + Number(i.arr_usd || 0), 0),
      inMetro: items.filter((i) => i.company_metro === payload.viewer_metro).length,
      orphans: items.filter((i) => i.score_breakdown?.orphan_flag === 1).length,
    };
  }, [payload]);

  const toggle = (dealId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  // Access gate — must be a whitelisted Copilot user
  if (!allowedViewer) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Inter', sans-serif", background: '#fff', color: COLORS.navy,
      }}>
        <div style={{
          maxWidth: 480, padding: 32, textAlign: 'center',
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(11,27,58,0.08)', borderRadius: 20,
          boxShadow: '0 20px 60px -20px rgba(11,27,58,0.15)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Copilot access restricted</div>
          <div style={{ fontSize: 13, color: COLORS.gray500, marginBottom: 20, lineHeight: 1.5 }}>
            Your account (<code style={{ background: COLORS.gray50, padding: '2px 6px', borderRadius: 4 }}>{loggedInEmail || 'unknown'}</code>) is not on the Copilot beta.
            <br/>Ask Alon or Jay to add you.
          </div>
          <button onClick={() => { window.location.href = '/'; }} style={{
            padding: '10px 20px', background: COLORS.navy, color: '#fff',
            border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Inter', sans-serif",
          }}>Back to main dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, sans-serif",
      background: '#fff',
      backgroundImage:
        'radial-gradient(at 20% 10%, rgba(0,203,192,0.06) 0px, transparent 50%),' +
        'radial-gradient(at 80% 90%, rgba(116,60,247,0.05) 0px, transparent 50%)',
      color: COLORS.navy,
      padding: '24px 16px 80px',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(11,27,58,0.06)',
        }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
              Copilot · Morning Queue
            </div>
            <div style={{
              fontSize: 11, color: COLORS.gray500, marginTop: 4,
              fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ViewerPicker value={viewer} onChange={setViewer} />
            <SizePicker value={size} onChange={setSize} />
            <button onClick={load} disabled={loading} style={btnPrimary(loading)}>
              {loading ? '⟳ Loading' : '⟳ Refresh'}
            </button>
          </div>
        </div>

        {/* Stats strip */}
        {stats && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20,
          }}>
            <StatCard label="Prospects surfaced" value={stats.total} />
            <StatCard label="Pipeline surfaced"  value={usd(stats.totalArr)} color={COLORS.teal} />
            <StatCard label={`In your metro (${metroLabel(payload.viewer_metro)})`} value={stats.inMetro} color={COLORS.pink} />
            <StatCard label="Orphan deals" value={stats.orphans} color={COLORS.purple} />
          </div>
        )}

        {/* Pending next-actions (agent-like reminders) */}
        {pending && pending.count > 0 && (
          <PendingActionsWidget
            pending={pending}
            onResolve={resolvePending}
            onOpen={(dealId) => {
              const match = payload?.items?.find((x) => x.deal_id === dealId);
              if (match) setDrawerItem(match);
            }}
          />
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '14px 18px',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 12,
            color: COLORS.red,
            fontSize: 13,
            marginBottom: 16,
          }}>
            <strong>Queue failed:</strong> {error}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !payload && (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.gray500 }}>
            Loading queue…
          </div>
        )}

        {/* Rows */}
        {payload?.items && (
          <div style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.6)',
            borderRadius: 20,
            padding: 6,
            boxShadow: '0 12px 40px -16px rgba(11,27,58,0.1)',
          }}>
            {payload.items.map((item) => (
              <QueueRow
                key={item.deal_id}
                item={item}
                expanded={expanded.has(item.deal_id)}
                onToggle={() => toggle(item.deal_id)}
                onLog={(type, notes) => logTouch({ dealId: item.deal_id, touchType: type, notes })}
                onDraft={() => openDraft({ dealId: item.deal_id, channel: 'email', intent: 'initial_reengage' })}
                onOpenDrawer={() => setDrawerItem(item)}
              />
            ))}
            {payload.items.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: COLORS.gray500 }}>
                No prospects surfaced for this size. Try a larger queue.
              </div>
            )}
          </div>
        )}

        {/* Toast (with optional undo) */}
        {toast && (
          <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 100,
            padding: '12px 20px',
            borderRadius: 12,
            background: toast.kind === 'err' ? 'rgba(239,68,68,0.95)' : 'rgba(11,27,58,0.95)',
            color: '#fff',
            fontSize: 13, fontWeight: 500,
            boxShadow: '0 10px 30px -8px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(20px)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            {toast.message}
            {lastTouchForUndo && toast.kind !== 'err' && (Date.now() - lastTouchForUndo.at) < 4000 && (
              <button
                onClick={() => undoTouch(lastTouchForUndo.touch_id)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 11, fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em',
                }}
              >UNDO</button>
            )}
          </div>
        )}

        {/* Draft modal */}
        {draftOpen && (
          <DraftModal state={draftOpen} onClose={() => setDraftOpen(null)} />
        )}

        {/* Deal drawer */}
        {drawerItem && (
          <DealDrawer
            item={drawerItem}
            viewer={viewer}
            email={loggedInEmail}
            onClose={() => setDrawerItem(null)}
            onLogged={() => { load(); loadPending(); }}
          />
        )}

        {/* Footer */}
        <div style={{
          textAlign: 'center', marginTop: 40, paddingTop: 16, borderTop: '1px solid rgba(11,27,58,0.05)',
          fontSize: 10, color: COLORS.gray400, fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          Octup Copilot · Sprint 1 · ?copilot=1
          {payload?.generated_at && ` · generated ${new Date(payload.generated_at).toLocaleTimeString()}`}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function QueueRow({ item, expanded, onToggle, onLog, onDraft, onOpenDrawer }) {
  const metroCol  = metroBadgeColor(item.company_metro);
  const actionCol = actionBadge(item.recommended_action);
  const phoneNumber = String(item.contact_phone || '').replace(/^'|"/g, '').trim();
  const cleanPhoneHref = phoneNumber.replace(/[^\d+]/g, '');
  const priorityScore = Math.round(Number(item.priority_score) || 0);
  const scoreColor = priorityScore >= 80 ? COLORS.pink
                  : priorityScore >= 60 ? COLORS.teal
                  : priorityScore >= 40 ? COLORS.purple
                  : COLORS.gray400;
  const [notesInput, setNotesInput] = React.useState('');
  const [showNoteBar, setShowNoteBar] = React.useState(false);
  const [pendingType, setPendingType] = React.useState(null);

  const doLog = (type) => {
    // If already showing note bar with a pending type, submit; otherwise show it.
    if (pendingType === type) {
      onLog(type, notesInput);
      setPendingType(null);
      setNotesInput('');
      setShowNoteBar(false);
    } else {
      setPendingType(type);
      setShowNoteBar(true);
    }
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(11,27,58,0.05)',
      borderRadius: 12,
      padding: '12px 16px',
      marginBottom: 4,
      transition: 'all 0.15s',
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr 200px 170px 130px 240px 30px',
        gap: 12,
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          background: `${scoreColor}15`,
          border: `1.5px solid ${scoreColor}40`,
          borderRadius: 10,
          padding: '4px 2px',
        }}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 16, fontWeight: 800,
            color: scoreColor,
            lineHeight: 1,
          }}>{priorityScore}</div>
          <div style={{
            fontSize: 8, color: COLORS.gray400, marginTop: 2,
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.05em',
          }}>#{item.queue_slot}</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <button
            onClick={onOpenDrawer}
            title="Open chat + timeline for this deal"
            style={{
              padding: 0, background: 'transparent', border: 'none',
              cursor: 'pointer', textAlign: 'left', width: '100%',
              fontFamily: 'inherit',
            }}
          >
            <div style={{
              fontWeight: 700, fontSize: 14, color: COLORS.navy, letterSpacing: '-0.01em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {item.company_name || '?'}
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: COLORS.gray400 }}>
                {usd(item.arr_usd)}
              </span>
              <span style={{ marginLeft: 6, fontSize: 11, color: COLORS.teal, opacity: 0.7 }}>
                ↗
              </span>
            </div>
            <div style={{
              fontSize: 11, color: COLORS.gray500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {item.contact_name || '(no contact)'}{item.contact_title ? ' · ' + item.contact_title : ''}
            </div>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {phoneNumber && (
            <a href={`tel:${cleanPhoneHref}`} style={pillLink}>☎ {phoneNumber}</a>
          )}
          {item.contact_email && (
            <a href={`mailto:${item.contact_email}`} style={pillLink} title={item.contact_email}>
              ✉
            </a>
          )}
          {item.hubspot_url && (
            <a href={item.hubspot_url} target="_blank" rel="noopener noreferrer" style={pillLink} title="Open in HubSpot">
              ◉ HubSpot
            </a>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            background: metroCol.bg,
            color: metroCol.color,
            textTransform: 'lowercase',
          }}>
            {metroLabel(item.company_metro)}
          </span>
          {item.score_breakdown?.orphan_flag === 1 && (
            <span style={{
              padding: '3px 8px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              background: 'rgba(239,68,68,0.08)',
              color: COLORS.red,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              Orphan
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            background: actionCol.bg,
            color: actionCol.color,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <span>{actionCol.icon}</span>
            <span style={{ textTransform: 'capitalize' }}>{item.recommended_action}</span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => doLog('call')}       style={actionBtn} title="Log call">☎</button>
          <button onClick={() => doLog('voicemail')}  style={actionBtn} title="Log voicemail">🎤</button>
          <button onClick={() => doLog('email_sent')} style={actionBtn} title="Log email sent">✉</button>
          <button onClick={() => doLog('sms')}        style={actionBtn} title="Log text">💬</button>
          <button onClick={() => doLog('linkedin_message')} style={actionBtn} title="Log LinkedIn">in</button>
          <button onClick={() => doLog('note')}       style={actionBtn} title="Log note">📝</button>
          <button onClick={onDraft} style={{ ...actionBtn, background: COLORS.navy, color: '#fff', paddingLeft: 10, paddingRight: 10, fontWeight: 600 }} title="AI-draft email in your voice">
            ✨ Draft
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onToggle} style={{
            background: 'transparent',
            border: 'none',
            fontSize: 12,
            color: COLORS.gray400,
            cursor: 'pointer',
            padding: '4px 6px',
          }}>
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>
      {showNoteBar && (
        <div style={{
          marginTop: 10, padding: 10,
          background: 'rgba(0,203,192,0.06)',
          border: '1px solid rgba(0,203,192,0.2)',
          borderRadius: 8,
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: COLORS.gray500, fontFamily: "'JetBrains Mono',monospace" }}>
            Logging <strong style={{ color: COLORS.teal }}>{pendingType?.replace('_', ' ')}</strong> — add a note?
          </span>
          <input
            type="text"
            value={notesInput}
            onChange={(e) => setNotesInput(e.target.value)}
            placeholder="Optional note (e.g. left VM about wallet feature)"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && doLog(pendingType)}
            style={{
              flex: 1, padding: '6px 10px', fontSize: 12,
              border: '1px solid rgba(11,27,58,0.1)', borderRadius: 6,
              fontFamily: "'Inter', sans-serif", color: COLORS.navy,
            }}
          />
          <button onClick={() => doLog(pendingType)} style={{ ...actionBtn, background: COLORS.teal, color: COLORS.navy, fontWeight: 700, paddingLeft: 10, paddingRight: 10 }}>Save</button>
          <button onClick={() => { setShowNoteBar(false); setPendingType(null); setNotesInput(''); }} style={{ ...actionBtn, color: COLORS.gray500 }}>×</button>
        </div>
      )}
      {expanded && (
        <ExpandBody item={item} />
      )}
    </div>
  );
}

const actionBtn = {
  padding: '4px 8px',
  fontSize: 12,
  background: COLORS.gray50,
  border: '1px solid transparent',
  borderRadius: 6,
  color: COLORS.gray700,
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.1s',
};

function ExpandBody({ item }) {
  const sb = item.score_breakdown || {};
  return (
    <div style={{
      marginTop: 12,
      paddingTop: 12,
      borderTop: '1px solid rgba(11,27,58,0.06)',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 12,
    }}>
      <div style={{
        background: COLORS.gray50,
        borderRadius: 10,
        padding: 12,
        fontSize: 12,
        color: COLORS.gray700,
        lineHeight: 1.5,
      }}>
        <div style={monoLabel}>Why now</div>
        <div style={{ marginTop: 4, color: COLORS.navy, fontWeight: 500 }}>
          {item.why_now || '—'}
        </div>
        {item.cross_over_hint && (
          <div style={{
            marginTop: 8,
            padding: '6px 10px',
            background: 'rgba(0,203,192,0.08)',
            borderRadius: 8,
            fontSize: 11,
            color: COLORS.teal,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            🎯 Cross-over: {item.cross_over_hint}
          </div>
        )}
        {item.last_touch && (
          <div style={{ marginTop: 8, fontSize: 11, color: COLORS.gray500 }}>
            Last touch: {item.last_touch.type} on {new Date(item.last_touch.at).toLocaleDateString()}
          </div>
        )}
      </div>
      <div style={{
        background: COLORS.gray50,
        borderRadius: 10,
        padding: 12,
        fontSize: 12,
      }}>
        <div style={monoLabel}>Score breakdown</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 4, marginTop: 6 }}>
          <ScoreLine label="ARR"                  value={usd(sb.arr_component)} />
          <ScoreLine label="Win probability"       value={((sb.win_prob_past || 0) * 100).toFixed(1) + '%'} />
          <ScoreLine label="Seniority weight"      value={'×' + Number(sb.seniority_weight || 1).toFixed(1)} />
          <ScoreLine label="Location multiplier"   value={'×' + Number(sb.location_multiplier || 1).toFixed(1)} />
          <ScoreLine label="Orphan boost"          value={sb.orphan_flag ? '×2.5' : '×1.0'} />
          <ScoreLine label="Days since last touch" value={String(sb.days_since_lost || 0) + 'd'} />
          <ScoreLine label="Sentiment"             value={Number(sb.sentiment_score || 0).toFixed(2)} />
          <div style={{ borderTop: '1px solid rgba(11,27,58,0.08)', gridColumn: '1 / -1', margin: '4px 0' }} />
          <ScoreLine label="Reactivation score" value={Math.round(item.reactivation_score || 0)} bold />
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small UI atoms
// -----------------------------------------------------------------------------

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.8)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255,255,255,0.6)',
      borderRadius: 20,
      padding: '14px 18px',
      boxShadow: '0 10px 30px -12px rgba(11,27,58,0.08)',
    }}>
      <div style={{
        fontSize: 10, color: COLORS.gray500, letterSpacing: '0.08em',
        textTransform: 'uppercase', fontWeight: 500,
        fontFamily: "'JetBrains Mono', monospace",
      }}>{label}</div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 24, fontWeight: 700,
        color: color || COLORS.navy,
        marginTop: 4,
        letterSpacing: '-0.02em',
      }}>{value}</div>
    </div>
  );
}

function ViewerPicker({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{
      padding: '8px 14px',
      border: '1px solid rgba(11,27,58,0.1)',
      borderRadius: 12,
      background: '#fff',
      color: COLORS.navy,
      fontFamily: "'Inter', sans-serif",
      fontSize: 13, fontWeight: 500,
      cursor: 'pointer',
    }}>
      <option value="alon">Alon (LA)</option>
      <option value="jay">Jay (Austin)</option>
    </select>
  );
}

function SizePicker({ value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} style={{
      padding: '8px 14px',
      border: '1px solid rgba(11,27,58,0.1)',
      borderRadius: 12,
      background: '#fff',
      color: COLORS.navy,
      fontFamily: "'Inter', sans-serif",
      fontSize: 13, fontWeight: 500,
      cursor: 'pointer',
    }}>
      {[5, 8, 10, 15, 20].map((n) => (
        <option key={n} value={n}>Show {n}</option>
      ))}
    </select>
  );
}

const monoLabel = {
  fontSize: 10, color: COLORS.gray500, letterSpacing: '0.08em',
  textTransform: 'uppercase', fontWeight: 600,
  fontFamily: "'JetBrains Mono', monospace",
};

const pillLink = {
  color: COLORS.gray700,
  textDecoration: 'none',
  padding: '3px 8px',
  background: COLORS.gray50,
  borderRadius: 6,
  fontSize: 11,
  fontFamily: "'JetBrains Mono', monospace",
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
};

function btnPrimary(loading) {
  return {
    padding: '8px 16px',
    background: loading ? COLORS.gray400 : COLORS.navy,
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 13, fontWeight: 600,
    cursor: loading ? 'wait' : 'pointer',
    fontFamily: "'Inter', sans-serif",
    boxShadow: '0 4px 12px -4px rgba(11,27,58,0.3)',
  };
}

function ScoreLine({ label, value, bold }) {
  return (
    <>
      <div style={{ color: COLORS.gray500, fontWeight: bold ? 700 : 400 }}>{label}</div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        color: bold ? COLORS.pink : COLORS.navy,
        fontWeight: bold ? 700 : 500,
        textAlign: 'right',
      }}>{value}</div>
    </>
  );
}

function PendingActionsWidget({ pending, onResolve, onOpen }) {
  const [expanded, setExpanded] = React.useState(false);
  const overdueColor = pending.overdue_count > 0 ? COLORS.red : COLORS.teal;
  const shown = expanded ? pending.items : pending.items.slice(0, 3);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(20px)',
      border: `1.5px solid ${overdueColor}30`,
      borderRadius: 20,
      padding: 18,
      marginBottom: 20,
      boxShadow: '0 10px 30px -12px rgba(11,27,58,0.08)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: shown.length ? 12 : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${overdueColor}15`, color: overdueColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700,
          }}>
            {pending.overdue_count > 0 ? '⚠' : '✓'}
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>
              {pending.overdue_count > 0
                ? `${pending.overdue_count} overdue commitment${pending.overdue_count === 1 ? '' : 's'}`
                : `${pending.count} pending action${pending.count === 1 ? '' : 's'}`}
              {pending.due_today_count > 0 && (
                <span style={{ marginLeft: 8, color: COLORS.teal, fontSize: 12 }}>
                  · {pending.due_today_count} due today
                </span>
              )}
            </div>
            <div style={{
              fontSize: 10, color: COLORS.gray500, fontFamily: "'JetBrains Mono',monospace",
              letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 2,
            }}>
              things you said you'd do
            </div>
          </div>
        </div>
        {pending.items.length > 3 && (
          <button onClick={() => setExpanded((x) => !x)} style={{
            padding: '5px 12px', fontSize: 11, fontWeight: 600,
            background: 'transparent', border: '1px solid rgba(11,27,58,0.15)',
            borderRadius: 999, cursor: 'pointer', color: COLORS.gray700,
            fontFamily: "'Inter',sans-serif",
          }}>
            {expanded ? 'Show top 3' : `Show all ${pending.items.length}`}
          </button>
        )}
      </div>
      {shown.map((p) => (
        <div key={p.source_touch_id} style={{
          display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 10,
          alignItems: 'center', padding: '10px 12px',
          background: p.status === 'overdue' ? 'rgba(239,68,68,0.06)'
                    : p.status === 'due_today' ? 'rgba(0,203,192,0.06)'
                    : COLORS.gray50,
          borderRadius: 10, marginBottom: 4, fontSize: 13,
        }}>
          <div style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 700,
            fontFamily: "'JetBrains Mono',monospace", letterSpacing: '0.05em',
            textTransform: 'uppercase',
            background: p.status === 'overdue' ? 'rgba(239,68,68,0.15)'
                      : p.status === 'due_today' ? 'rgba(0,203,192,0.15)'
                      : 'rgba(107,114,128,0.1)',
            color: p.status === 'overdue' ? COLORS.red
                 : p.status === 'due_today' ? COLORS.teal
                 : COLORS.gray500,
            borderRadius: 6,
          }}>
            {p.status === 'overdue' ? `${p.days_overdue}d late`
             : p.status === 'due_today' ? 'today'
             : `in ${Math.abs(p.days_overdue)}d`}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: COLORS.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.action_type ? p.action_type.replace(/_/g, ' ') : '(no action type)'}
              <span style={{ marginLeft: 8, color: COLORS.gray500, fontWeight: 400, fontSize: 12 }}>
                {p.company_name} · {p.contact_name}
              </span>
            </div>
            {p.notes && (
              <div style={{
                fontSize: 11, color: COLORS.gray500, marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{p.notes}</div>
            )}
          </div>
          <button onClick={() => onOpen(p.deal_id)} style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 500,
            background: 'transparent', border: '1px solid rgba(11,27,58,0.15)',
            borderRadius: 6, cursor: 'pointer', color: COLORS.gray700,
            fontFamily: "'Inter',sans-serif",
          }}>Open</button>
          <button onClick={() => onResolve(p.source_touch_id)} style={{
            padding: '4px 10px', fontSize: 11, fontWeight: 600,
            background: COLORS.teal, border: 'none',
            borderRadius: 6, cursor: 'pointer', color: COLORS.navy,
            fontFamily: "'Inter',sans-serif",
          }}>✓ Done</button>
        </div>
      ))}
    </div>
  );
}

function DraftModal({ state, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const copy = (text) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(11,27,58,0.5)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 20, padding: 24,
        maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 30px 80px -20px rgba(11,27,58,0.4)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.navy }}>
            ✨ AI Draft — deal {state.deal_id}
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', fontSize: 20,
            color: COLORS.gray400, cursor: 'pointer',
          }}>×</button>
        </div>

        {state.loading && (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.gray500 }}>
            Calling Claude...
          </div>
        )}
        {state.error && (
          <div style={{
            padding: 14, borderRadius: 10,
            background: 'rgba(239,68,68,0.08)', color: COLORS.red,
            border: '1px solid rgba(239,68,68,0.25)', fontSize: 13,
          }}>
            <strong>Draft failed:</strong> {state.error}
            <div style={{ marginTop: 8, fontSize: 11, color: COLORS.gray500 }}>
              Common cause: ANTHROPIC_API_KEY not set on the server. Set it and try again.
            </div>
          </div>
        )}
        {state.data && (
          <div>
            <div style={{ marginBottom: 12, fontSize: 12, color: COLORS.gray500 }}>
              <span style={{ padding: '3px 8px', background: 'rgba(0,203,192,0.1)', color: COLORS.teal, borderRadius: 6, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>
                {state.data.staleness_classification}
              </span>
              {' · '}
              <span style={{ color: COLORS.navy, fontWeight: 500 }}>{state.data.action_recommendation}</span>
              {' · '}
              voice: <strong>{state.data.voice_of}</strong>
              {state.data.tokens_used && (
                <span style={{ float: 'right', color: COLORS.gray400 }}>
                  ${state.data.tokens_used.cost_usd?.toFixed(4) || '?'}
                </span>
              )}
            </div>
            <div style={{ marginBottom: 12, fontSize: 12, color: COLORS.gray700, fontStyle: 'italic', padding: 10, background: COLORS.gray50, borderRadius: 8 }}>
              <strong>Underlying truth:</strong> {state.data.underlying_truth}
            </div>
            {state.data.subject_options && state.data.subject_options.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: COLORS.gray500, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>
                  Subject options
                </div>
                {state.data.subject_options.map((s, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', marginBottom: 4,
                    background: i === 0 ? 'rgba(0,203,192,0.06)' : COLORS.gray50,
                    borderLeft: i === 0 ? `3px solid ${COLORS.teal}` : `3px solid transparent`,
                    borderRadius: 6, fontSize: 13, color: COLORS.navy,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span>{i === 0 && <strong style={{ color: COLORS.teal, marginRight: 6 }}>▶</strong>}{s}</span>
                    <button onClick={() => copy(s)} style={{ ...actionBtn, fontSize: 10 }}>copy</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: COLORS.gray500, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span>Body</span>
                <button onClick={() => copy(state.data.body)} style={{ ...actionBtn, fontSize: 10 }}>
                  {copied ? '✓ copied' : 'copy body'}
                </button>
              </div>
              <div style={{
                padding: 14, background: COLORS.gray50, borderRadius: 8,
                fontSize: 13, color: COLORS.navy, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                fontFamily: "'Inter', sans-serif",
              }}>
                {state.data.body}
              </div>
            </div>
            {state.data.rationale && (
              <div style={{ marginTop: 10, fontSize: 11, color: COLORS.gray500 }}>
                <em>{state.data.rationale}</em>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
